import Model from "./Model.ts";
import {ApplicationType} from "../types/ApplicationType.ts";
import {useCookies} from "../composables/useCookies.ts";
import {PaginatedData} from "../types/PaginatedData.ts";
import User from "./User.ts";
import {cleanQuery} from "../utils";
import {CallbackURL} from "./CallbackURL.ts";
import api from "../api.ts";

export interface ApplicationSummaryMetric {
    id: string;
    timestamp: string;
    client_id: string | null;
    user_id: string | null;
    event_type: string;
    grant_type: string | null;
    ip_address: string | null;
    response_time_ms: number | null;
    details: Record<string, any> | null;
}

export type ApplicationSummary = Record<string, ApplicationSummaryMetric[]>;

/**
 * Represents an application in the system.
 *
 * This class encapsulates the details of an application, including its metadata,
 * status, type, and associated operations such as creation, update, activation,
 * and deletion. It extends the `Model` class for consistent ID handling.
 *
 * @author Jackson Bingham
 * @date December 2024
 */
class Application extends Model {
    /** The client ID associated with the application. */
    private readonly _client_id: string;

    /** The secret key for the application. */
    private readonly _secret: string;

    /** The title of the application. */
    private _title: string;

    /** The description of the application. */
    private _description: string;

    /** The URL associated with the application. */
    private _url: string;

    /**
     * The email whitelabel configurations for the application
     * @private
     */
    private _email_whitelabel: { company_name: string };

    /** The status of the application: 0 for inactive, 1 for active. */
    private _status: 0 | 1;

    /** The type of the application (`prod`, `beta`, `dev`). */
    private _type: ApplicationType;

    /** Whether the application is marked as legacy: 0 for false, 1 for true. */
    private _legacy: 0 | 1;

    private readonly _created_at: Date;

    public static async fetch({
                                  page,
                                  itemsPerPage,
                                  sortBy,
                                  search
                              }): Promise<[boolean, PaginatedData<Application>, Error?]> {

        const tokenCookie = useCookies().getCookie("access_token");

        try {
            const cleanedQuery: string = cleanQuery(search || '');
            const response = await api.get(
                `/api/v4/applications?page=${page}&limit=${itemsPerPage}&sort=${sortBy}&search=${cleanedQuery}`,
                {
                    withCredentials: true,
                    headers: {
                        Authorization: `Bearer ${tokenCookie}`,
                    }
                });

            const rawData = response.data;

            const data: PaginatedData<Application> = {
                ...rawData,
                data: rawData.data.map((app: any) => Application.fromJson(app))
            }

            return [true, data];
        } catch (error) {
            return [false, null, error];
        }
    }

    public static async fetchById(id: string): Promise<[boolean, Application, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            const response = await api.get(`/api/v4/applications/${id}`, {
                withCredentials: true,
                headers: {
                    Authorization: `Bearer ${tokenCookie}`,
                }
            })

            const application = Application.fromJson(response.data.application);
            return [true, application];
        } catch (error) {
            return [false, null, error];
        }
    }

    public static async fetchByClientId(clientId: string): Promise<[boolean, Application, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            const response = await api.get(`/api/v4/applications/client/${clientId}`, {
                withCredentials: true,
                headers: {
                    Authorization: `Bearer ${tokenCookie}`,
                }
            })

            const application = Application.fromJson(response.data.application);
            return [true, application];
        } catch (error) {
            return [false, null, error];
        }
    }

    /**
     * Creates a new application by sending a POST request to the server.
     *
     * @param values - The form data required to create the application.
     * @returns A Promise that resolves to a tuple:
     * - The first element is a boolean indicating whether the creation was successful (`true`) or not (`false`).
     * - The second element is the newly created `Application` instance, or `null` if the creation failed.
     * - The third element is an `Error` object if the creation failed, or `undefined` if it succeeded.
     *
     * @example
     * ```ts
     * const [success, newApp, error] = await Application.create({
     *   title: "New App",
     *   description: "An awesome app.",
     *   url: "https://example.com",
     *   status: 1,
     *   type: "dev",
     *   legacy: 0
     * });
     *
     * if (success) {
     *   console.log(newApp.title); // "New App"
     * } else {
     *   console.error(error.message);
     * }
     * ```
     */
    public static async create(values: IApplicationForm): Promise<[boolean, Application, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            const response = await api.post(
                `/api/v4/applications`,
                values,
                {
                    withCredentials: true,
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${tokenCookie}`,
                    },
                }
            );

            const application: Application = Application.fromJson(response.data.application);
            return [true, application];
        } catch (error) {
            return [false, null, error];
        }
    }

    /**
     * Retrieves a single application by its ID from the server.
     *
     * @param id - The unique identifier of the application to retrieve.
     *
     * @returns A Promise that resolves to a tuple:
     * - The first element is a boolean indicating success or failure.
     * - The second element is the application instance on success, or `null` on failure.
     * - The third element is an error object if the request fails.
     *
     * @example
     * ```ts
     * const [success, application, error] = await Application.getApplicationById("12345");
     *
     * if (success) {
     *   console.log(application.name); // Outputs the name of the application
     * } else {
     *   console.error(error); // Outputs the error
     * }
     * ```
     */
    public static async getApplicationById(id: string): Promise<[boolean, Application, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            const response = await api.get(`/api/v4/applications/${id}`, {
                withCredentials: true,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${tokenCookie}`,
                },
            })

            const application = Application.fromJson(response.data.application);

            return [true, application];
        } catch (error) {
            return [false, null, error];
        }
    }

    /**
     * Creates an `Application` instance from a JSON object.
     *
     * @param json - The JSON object representing the application.
     * @returns A new `Application` instance.
     *
     * @example
     * ```ts
     * const json = { id: "123", client_id: "client123", ... };
     * const app = Application.fromJson(json);
     * console.log(app.client_id); // "client123"
     * ```
     */
    public static fromJson(json: any): Application {
        return new Application(
            json.id,
            json.client_id,
            json.secret,
            json.title,
            json.description,
            json.url,
            {
                company_name: json.email_whitelabel?.company_name || '',
            },
            json.status,
            json.type,
            json.legacy,
            json.created_at
        )
    }

    /**
     * Constructs a new Application instance.
     *
     * @param id - The unique identifier for the application.
     * @param client_id - The client ID associated with the application.
     * @param secret - The secret key for the application.
     * @param title - The title of the application.
     * @param description - The description of the application.
     * @param url - The CallbackURL associated with the application.
     * @param email_whitelabel
     * @param status - The status of the application: 0 for inactive, 1 for active.
     * @param type - The type of the application.
     * @param legacy - Whether the application is marked as legacy: 0 for false, 1 for true.
     * @param created_at - The date the application was put into the system.
     *
     * @example
     * ```ts
     * const app = new Application(
     *   "123",
     *   "client_123",
     *   "secret_123",
     *   "My App",
     *   "This is a test application.",
     *   "https://example.com",
     *   1,
     *   "dev",
     *   0,
     *   "04/20/2024"
     * );
     * console.log(app.title); // "My App"
     * ```
     */
    constructor(
        id: string,
        client_id: string,
        secret: string,
        title: string,
        description: string,
        url: string,
        email_whitelabel: { company_name: string },
        status: 0 | 1,
        type: ApplicationType,
        legacy: 0 | 1,
        created_at: string,
    ) {
        super(id);
        this._client_id = client_id;
        this._secret = secret;
        this._title = title;
        this._description = description;
        this._url = url;
        this._email_whitelabel = email_whitelabel,
            this._status = status;
        this._type = type;
        this._legacy = legacy;
        this._created_at = new Date(created_at);
    }

    /**
     * Gets the client id of the application.
     *
     * @returns the client id of the application
     */
    public get client_id(): string {
        return this._client_id;
    }

    public get secret(): string {
        return this._secret;
    }

    public get title(): string {
        return this._title;
    }

    public get description(): string {
        return this._description;
    }

    public get url(): string {
        return this._url;
    }

    public get email_whitelabel(): { company_name: string } {
        return this._email_whitelabel;
    }

    public get status(): 0 | 1 {
        return this._status;
    }

    public set status(value: 0 | 1) {
        this._status = value;
    }

    public get type(): ApplicationType {
        return this._type;
    }

    public get legacy(): 0 | 1 {
        return this._legacy;
    }

    public get created_at(): Date {
        return this._created_at;
    }

    /**
     * Converts the `Application` instance to a plain JSON object.
     *
     * This method overrides the base `toJSON` implementation to include the `title`, `client_id`,
     * `secret`, `description`, `url`, `status`, `type`, and `legacy` fields.
     *
     * @override
     * @returns An object containing the `Application`'s `id`, fields.
     *
     * @example
     * ```ts
     * const app = new Application({id: "123", title: "MyApp",  ...});
     * console.log(app.toJSON());
     * // { id: "123", title: "MyApp" }
     * ```
     */
    public override toJSON() {
        return {
            id: this.id,
            client_id: this._client_id,
            secret: this._secret,
            title: this._title,
            description: this._description,
            url: this._url,
            email_whitelabel: {
                company_name: this._email_whitelabel.company_name,
            },
            status: this._status,
            type: this._type,
            legacy: this._legacy
        }
    }

    /**
     * Updates the application by sending a PATCH request to the server.
     *
     * @param values - The form data containing the updated application details.
     * @returns A Promise that resolves to a tuple:
     * - The first element is a boolean indicating success (`true`) or failure (`false`).
     * - The second element is the updated `Application` instance or `null` on failure.
     * - The third element is the error (if any) on failure.
     *
     * @example
     * ```ts
     * const [success, updatedApp, error] = await app.update({
     *   title: "Updated Title",
     *   description: "Updated description",
     *   url: "https://updated.com"
     * });
     *
     * if (success) {
     *   console.log(updatedApp.title); // "Updated Title"
     * } else {
     *   console.error(error.message);
     * }
     * ```
     */
    public async update(values: IApplicationForm): Promise<[boolean, Application, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            const response = await api.patch(
                `/api/v4/applications/${this.id}`,
                values,
                {
                    withCredentials: true,
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${tokenCookie}`,
                    },
                }
            );

            // Update local instance with the response data
            const updated = Application.fromJson(response.data.application);
            Object.assign(this, updated);
            // this._title = response.data.app.title;
            // this._description = response.data.app.description;
            // this._url = response.data.app.url;
            // this._email_whitelabel = response.data.app.email_whitelabel;
            // this._status = response.data.app.status;
            // this._type = response.data.app.type;
            // this._legacy = response.data.app.legacy;

            return [true, this];
        } catch (error) {
            return [false, null, error];
        }
    }

    /**
     * Deletes the application by sending a DELETE request to the server.
     *
     * @returns A Promise that resolves to a tuple:
     * - The first element is a boolean indicating whether the deletion was successful (`true`) or not (`false`).
     * - The second element is an `Error` object if the deletion failed, or `undefined` if it succeeded.
     *
     * @example
     * ```ts
     * const [success, error] = await app.destroy();
     *
     * if (success) {
     *   console.log("Application successfully deleted");
     * } else {
     *   console.error(error.message);
     * }
     * ```
     */
    public async destroy(): Promise<[boolean, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");
        try {
            // Send DELETE request to the server
            await api.delete(`/api/v4/applications/${this.id}`, {
                withCredentials: true,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${tokenCookie}`,
                },
            });

            return [true];
        } catch (error) {
            console.error("Error deleting role:", error);
            return [false, error];
        }
    }

    public async deactivate(): Promise<[boolean, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            await api.post(
                `/api/v4/applications/${this.id}/deactivate`, {}, {
                    withCredentials: true,
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${tokenCookie}`,
                    },
                }
            );

            return [true]
        } catch (error) {
            console.error(error);
            return [false, error];
        }
    }

    public async activate(): Promise<[boolean, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            await api.post(
                `/api/v4/applications/${this.id}/activate`, {}, {
                    withCredentials: true,
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${tokenCookie}`,
                    },
                }
            );

            return [true]
        } catch (error) {
            console.error(error);
            return [false, error];
        }
    }

    public async fetchMetrics(): Promise<[boolean, ApplicationSummary, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            const response = await api.get(`/api/v4/applications/${this.id}/summary`, {
                withCredentials: true,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${tokenCookie}`,
                },
            });

            const metrics = response.data.metrics as ApplicationSummary;

            return [true, metrics];
        } catch (error) {
            return [false, null, error];
        }
    }

    public async searchUsers(query: string): Promise<[boolean, User[], Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");
        const cleanedQuery = cleanQuery(query);
        try {

            const response = await api.get(`/api/oix/users`, {
                headers: {
                    Authorization: `Bearer ${tokenCookie}`,
                },
                params: {
                    client_id: this.client_id,
                    search: cleanedQuery,
                },
            });

            const users: User[] = response.data.users.map((user: any) => User.fromJson(user));

            return [true, users];
        } catch (error) {
            return [false, null, error];
        }
    }

    public async getUsers({
                              page,
                              itemsPerPage,
                              sortBy,
                              search
                          }): Promise<[boolean, PaginatedData<User>, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");
        try {
            const cleanedQuery: string = cleanQuery(search || '');
            const response = await api.get<PaginatedData<User>>(
                `/api/v4/users?page=${page}&limit=${itemsPerPage}&sort=${sortBy}&search=${cleanedQuery}`, {
                    headers: {
                        Authorization: `Bearer ${tokenCookie}`,
                    },
                    params: {
                        app_id: this.id,
                    },
                });

            const rawData = response.data;

            const data: PaginatedData<User> = {
                ...rawData,
                data: rawData.data.map((user: any) => User.fromJson(user)), // Construct each user into an object
            };

            return [true, data];
        } catch (error) {
            return [false, null, error];
        }
    }

    public async getCallbackUrls({
                                     page,
                                     itemsPerPage,
                                     sortBy,
                                     search
                                 }): Promise<[boolean, PaginatedData<CallbackURL>, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");
        try {
            const cleanedQuery: string = cleanQuery(search || '');
            const response = await api.get<PaginatedData<User>>(
                `/api/v4/callback-urls?page=${page}&limit=${itemsPerPage}&sort=${sortBy}&search=${cleanedQuery}`, {
                    headers: {
                        Authorization: `Bearer ${tokenCookie}`,
                    },
                    params: {
                        app_id: this.id,
                    },
                });

            const rawData = response.data;

            const data: PaginatedData<CallbackURL> = {
                ...rawData,
                data: rawData.data.map((url: any) => CallbackURL.fromJson(url))
            }

            return [true, data];
        } catch (error) {
            return [false, null, error];
        }
    }

    public async updateEmailWhitelabel({company_name}: {
        company_name: string
    }): Promise<[boolean, Application, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");
        try {
            const response = await api.patch(
                `/api/v4/applications/${this.id}/email-whitelabel`,
                {
                    company_name: company_name,
                },
                {
                    headers: {
                        Authorization: `Bearer ${tokenCookie}`,
                    },
                }
            )

            const updated = Application.fromJson(response.data.application);

            return [true, updated];
        } catch (error) {
            return [false, undefined, error];
        }
    }

    public async rotateSecret(): Promise<[boolean, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            const response = await api.post(`/api/v4/applications/${this.id}/rotate-secret`, {}, {
                headers: {
                    Authorization: `Bearer ${tokenCookie}`,
                },
            })

            const updated = Application.fromJson(response.data.application);
            Object.assign(this, updated);
            return [true, undefined];
        } catch (error) {
            return [false, error];
        }
    }

    public async grantAccessToUsers(user_ids: string[]): Promise<[boolean, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            await api
                .post(`/api/v4/applications/${this.id}/authorize-users`, {
                    user_ids
                }, {
                    headers: {
                        Authorization: `Bearer ${tokenCookie}`,
                    },
                });

            return [true, undefined];
        } catch (error) {
            return [false, error];
        }
    }

    public async removeAccessFromUsers(user_ids: string[]): Promise<[boolean, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            await api
                .post(`/api/v4/applications/${this.id}/unauthorize-users`, {
                    user_ids
                }, {
                    headers: {
                        Authorization: `Bearer ${tokenCookie}`,
                    },
                });

            return [true, undefined];
        } catch (error) {
            return [false, error];
        }
    }
}

export default Application;

export interface IApplicationForm {
    title: string;
    description: string;
    url: string;
    status: number;
    type: ApplicationType;
    legacy: number;
}
